import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { MdSkipNext, MdStop } from 'react-icons/md';
import { Button, OrbBuddies, ORB_TIPS_ABOVE, BUDDIES_PAIR } from '@aibuild/ui';
import { useElementSize } from '@hooks/useElementSize';
import {
  SKETCH_GUIDE_NARROW_PX,
  SKETCH_GUIDE_PX,
  SKETCH_MESSAGES,
  SKETCH_STEP_MS,
} from './sketch.constants';
import { sketchLayout, type SketchLayout } from './sketchLayout';
import { sketchTimeline, type SketchFocus } from './sketchTimeline';
import SketchDrawing from './SketchDrawing';
import { useAppSketch } from './useAppSketch';
import { Guide, SketchActions, SketchRoot } from './BuildSketch.styled';

interface BuildSketchProps {
  readonly appKey: string;
  readonly brief: string;
  readonly onDone: () => void;
  readonly onStop: () => void;
}

interface Point {
  readonly x: number;
  readonly y: number;
}

/** Where the buddies stand, feet first, while they draw the part in focus. */
const guideFoot = (focus: SketchFocus | null, layout: SketchLayout, guide: number): Point => {
  const { frame, viewers, controls, exportPill, heading } = layout;
  const beside = (y: number): Point =>
    layout.narrow ? { x: heading.x + heading.w - guide / 2, y } : { x: heading.x - guide * 0.4, y };
  switch (focus?.kind) {
    case 'viewer': {
      const box = viewers[0]?.box ?? frame;
      return { x: box.x + guide * 0.6, y: box.y + box.h - 12 };
    }
    case 'control': {
      const box = controls[focus.index]?.box ?? heading;
      return beside(box.y + box.h);
    }
    case 'export':
      return beside(exportPill.y + exportPill.h);
    default:
      return { x: frame.x + frame.w / 2, y: frame.y + frame.h * 0.62 };
  }
};

const clampGuide = ({ x, y }: Point, width: number, height: number, guide: number): Point => ({
  x: Math.min(Math.max(x, guide / 2 + 8), width - guide / 2 - 8) - guide / 2,
  y: Math.min(Math.max(y, guide), height - 8) - guide * 0.6,
});

const BuildSketch: React.FC<BuildSketchProps> = ({ appKey, brief, onDone, onStop }) => {
  const intl = useIntl();
  const rootRef = useRef<HTMLDivElement>(null);
  const { width, height } = useElementSize(rootRef);
  const sketch = useAppSketch(appKey, brief);
  const [step, setStep] = useState(0);
  const doneRef = useRef(onDone);

  useEffect(() => {
    doneRef.current = onDone;
  }, [onDone]);

  const layout = useMemo(
    () => (sketch && width > 0 ? sketchLayout(width, height, sketch) : null),
    [height, sketch, width],
  );
  const drawn = layout ? layout.controls.length : 0;
  const timeline = useMemo(
    () => (sketch && layout ? sketchTimeline(intl, { ...sketch, controls: layout.controls }) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the steps depend on how many controls fit, not where
    [intl, sketch, drawn],
  );

  const started = timeline.length > 0;
  useEffect(() => {
    if (!started) return undefined;
    const last = timeline.length - 1;
    const id = window.setTimeout(() => (step >= last ? doneRef.current() : setStep(step + 1)), SKETCH_STEP_MS);
    return () => window.clearTimeout(id);
  }, [started, step, timeline.length]);

  const current = timeline[Math.min(step, timeline.length - 1)] ?? null;
  const guide = layout?.narrow ? SKETCH_GUIDE_NARROW_PX : SKETCH_GUIDE_PX;
  const position = layout
    ? clampGuide(guideFoot(current?.focus ?? null, layout, guide), width, height, guide)
    : { x: width / 2 - guide / 2, y: height / 2 - guide / 2 };
  const tips = useMemo(
    () => [current?.text ?? intl.formatMessage(SKETCH_MESSAGES.reading)],
    [current?.text, intl],
  );

  return (
    <SketchRoot ref={rootRef} data-testid="app-builder__sketch" data-step={started ? step : undefined}>
      {layout && (
        <SketchDrawing
          width={width}
          height={height}
          layout={layout}
          revealed={started ? step + 1 : 0}
          headingLabel={intl.formatMessage(SKETCH_MESSAGES.controls)}
          exportLabel={intl.formatMessage(SKETCH_MESSAGES.exportLabel)}
        />
      )}
      <SketchActions>
        <Button
          variant="outlined"
          size="sm"
          leadingIcon={<MdStop size={16} aria-hidden="true" />}
          onClick={onStop}
          dataTestId="app-builder__build-stop"
        >
          {intl.formatMessage(SKETCH_MESSAGES.stop)}
        </Button>
        <Button
          variant="filled"
          size="sm"
          leadingIcon={<MdSkipNext size={16} aria-hidden="true" />}
          onClick={onDone}
          dataTestId="app-builder__sketch-skip"
        >
          {intl.formatMessage(SKETCH_MESSAGES.skip)}
        </Button>
      </SketchActions>
      {width > 0 && (
        <Guide $x={position.x} $y={position.y}>
          <OrbBuddies
            size={guide}
            who={BUDDIES_PAIR}
            busy
            network={false}
            tips={tips}
            tipPlacement={ORB_TIPS_ABOVE}
            dataTestId="app-builder__sketch-buddies"
          />
        </Guide>
      )}
    </SketchRoot>
  );
};

export default BuildSketch;
